import { useState, FormEvent } from "react";
import { Link } from "react-router-dom";
import { Loader2, MailCheck } from "lucide-react";
import { supabase } from "../../lib/supabaseClient";

const NAVY = "#2a2420";
const ORANGE = "#607840";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(
        email.trim().toLowerCase(),
        { redirectTo: `${window.location.origin}/reset-password` },
      );

      if (error) {
        setError("We couldn't send the reset link. Please try again.");
        return;
      }

      setSent(true);
    } catch {
      setError("We couldn't connect right now. Please check your internet connection and try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex h-screen w-screen overflow-hidden relative">

      {/* ── Left: brand panel ── */}
      <div
        className="hidden lg:block"
        style={{
          flex: "0 0 60%",
          backgroundImage: "url(/login-logo.png)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          backgroundColor: "#2a2420",
        }}
      />

      {/* ── Right: panel ── */}
      <div
        className="flex flex-col justify-center px-10 py-12 w-full lg:w-auto overflow-y-auto"
        style={{ flex: "0 0 40%", backgroundColor: "#fefcf8", borderLeft: "1px solid rgba(80,55,30,0.08)" }}
      >
        <div className="w-full max-w-xs mx-auto">

          {sent ? (
            <>
              <div
                className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-5"
                style={{ backgroundColor: "rgba(96,120,64,0.1)" }}
              >
                <MailCheck size={28} style={{ color: ORANGE }} />
              </div>
              <h1 className="text-2xl font-extrabold mb-1 text-center" style={{ color: NAVY }}>
                Check your email
              </h1>
              <p className="text-sm mb-8 text-center leading-relaxed" style={{ color: NAVY, opacity: 0.55 }}>
                If an account exists for <span className="font-semibold">{email.trim().toLowerCase()}</span>,
                we've sent a link to reset your password.
              </p>
              <Link
                to="/login"
                className="block w-full py-2.5 rounded-lg text-sm font-bold text-white transition-all text-center"
                style={{ backgroundColor: ORANGE, boxShadow: "0 3px 12px rgba(96,120,64,0.3)" }}
              >
                Back to sign in
              </Link>
            </>
          ) : (
            <>
              <h1 className="text-2xl font-extrabold mb-1 text-center" style={{ color: NAVY }}>
                Reset your password
              </h1>
              <p className="text-sm mb-8 text-center" style={{ color: NAVY, opacity: 0.5 }}>
                Enter your email and we'll send you a reset link.
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold mb-1.5 text-center" style={{ color: NAVY }}>
                    Email
                  </label>
                  <input
                    type="email"
                    required
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full px-3 py-2.5 rounded-lg text-sm outline-none transition-all"
                    style={{ border: `1.5px solid ${NAVY}22`, color: NAVY, backgroundColor: "#f8f3ea" }}
                    onFocus={(e) => (e.currentTarget.style.border = `1.5px solid ${ORANGE}`)}
                    onBlur={(e) => (e.currentTarget.style.border = `1.5px solid ${NAVY}22`)}
                  />
                </div>

                {error && (
                  <p className="text-sm text-center" style={{ color: "#991b1b" }}>
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-2.5 rounded-lg text-sm font-bold text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  style={{ backgroundColor: ORANGE, boxShadow: "0 3px 12px rgba(96,120,64,0.3)" }}
                >
                  {loading && <Loader2 size={14} className="animate-spin" />}
                  {loading ? "Sending…" : "Send reset link"}
                </button>
              </form>

              <p className="text-sm mt-8 text-center" style={{ color: NAVY, opacity: 0.55 }}>
                Remembered it?{" "}
                <Link to="/login" className="font-semibold" style={{ color: ORANGE }}>
                  Sign in
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
